/* eslint-disable no-empty */
import moment from 'moment';
import FileSaver from 'file-saver';
import { call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import SagaUtils from 'Utils/SagaUtils';
import { extractError } from 'Utils/ErrorUtils';
import { UsersActions } from './actions';
import { UsersSelectors } from './selectors';
import ApiService from '../../../Services/ApiService';
import { displayNotification } from '../../../Utils/GenericUtils';
import { writeToStorage } from '../../../Services/LocalStorageService';
import { localStorageKeys } from '../../../Constants/Common';

export function* fetchUsers() {
  try {
    yield put(UsersActions.usersFetchRequest());
    yield put(UsersActions.fetchUserSummary());

    const query = yield select(UsersSelectors.userFilterSelector);

    const users = yield call(ApiService.getUsers, { query });

    yield put(UsersActions.usersFetchSucceeded(users));
  } catch (e) {
    yield put(UsersActions.usersFetchFailed(e.response.data));
  }
}

export function* fetchUsersSummary() {
  try {
    const summary = yield call(ApiService.getUserSummary);

    yield put(UsersActions.fetchUserSummarySuccess(summary));
  } catch (e) {
    yield put(UsersActions.fetchUserSummaryFailed(e.response.data));
  }
}

export function* adminHandle(action) {
  try {
    const admin = action.payload;
    const res = yield call(ApiService.toggleAdmin, admin);
    yield put(UsersActions.adminHandleSucceeded(res));
    displayNotification('success', 'Success', 'User admin status was updated successfully');
  } catch (e) {
    yield put(UsersActions.adminHandleFailed());
    displayNotification('error', 'Error', "You can't remove yourself from the admin role");
  }
}

export function* searchAssignHandle(action) {
  try {
    const userToAssign = action.payload;
    const res = yield call(ApiService.assignToSearch, userToAssign);
    yield put(UsersActions.assignToSearchSucceeded(res));
    displayNotification('success', 'Success', 'User search status was updated successfully');
  } catch (e) {
    yield put(UsersActions.assignToSearchFailed());
    displayNotification('error', 'Error', 'an Error occurred assigning this subscription');
  }
}

export function* searchUnassignHandle(action) {
  try {
    const userToUnassign = action.payload;
    const res = yield call(ApiService.unassignToSearch, userToUnassign);
    yield put(UsersActions.unassignToSearchSucceeded(res));
    displayNotification('success', 'Success', 'User search status was updated successfully');
  } catch (e) {
    yield put(UsersActions.unassignToSearchFailed());
    displayNotification('error', 'Error', 'an Error occurred unassigning this subscription');
  }
}

export function* userStatusHandle(action) {
  try {
    const userIdToChange = action.payload;
    const res = yield call(ApiService.toggleActive, userIdToChange);

    yield put(UsersActions.changeUserStatusSucceeded(res));
    displayNotification('success', 'Success', 'User Status has been changed successfully');
  } catch (e) {
    yield put(UsersActions.changeUserStatusFailed(e.response.data));
    displayNotification('error', 'Error', 'Error updating user status');
  }
}

export function* editProfile(action) {
  try {
    const user = action.payload;
    const res = yield call(ApiService.editProfile, user);

    yield put(UsersActions.editProfileSucceeded(res));
    displayNotification('success', 'Success', 'Profile Details have been updated successfully');
  } catch (e) {
    yield put(UsersActions.editProfileFailed(e.response.data));
    displayNotification('error', 'Error', "Error updating user's Profile Details");
  }
}

export function* assignToTeams(action) {
  try {
    yield call(ApiService.assignToTeams, action.payload);

    yield put(UsersActions.assignToTeamsSucceeded(action.payload));
    displayNotification('success', 'Success', 'User updated successfully');
  } catch (e) {
    yield put(UsersActions.assignToTeamsFailed(e.response.data));
    displayNotification('error', 'Error', 'Error assigning user to teams');
  }
}

export function* bulkAssignToTeams(action) {
  try {
    yield call(ApiService.bulkAssignToTeams, action.payload);

    yield put(UsersActions.bulkAssignToTeamsSucceeded(action.payload));
    displayNotification('success', 'Success', 'Teams updated successfully');
  } catch (e) {
    yield put(UsersActions.bulkAssignToTeamsFailed(e.response.data));
    displayNotification('error', 'Error', 'Error assigning users to teams');
  }
}

function getUsersFromReportList(reportList) {
  const headlessReport = [...reportList];
  headlessReport.shift();
  return headlessReport.map((row) => {
    return {
      email: row[0],
      first_name: row[1],
      last_name: row[2],
      is_org_admin: row[3].toLowerCase().trim() === 'yes',
      assign_search: row[4].toLowerCase().trim() === 'yes',
    };
  });
}

export function* asyncBulkAddUsers() {
  try {
    const reportList = yield select(UsersSelectors.reportListSelector);
    const users = getUsersFromReportList(reportList);
    const res = yield call(ApiService.asyncBulkAddUsers, users);
    writeToStorage(localStorageKeys.USER_LIST_NOTIFICATION_IS_SHOWN, true);
    writeToStorage(localStorageKeys.USER_LIST_UPLOAD_IN_PROGRESS, true);
    yield put(UsersActions.closeUserListPendingNotice());
    yield put(UsersActions.addUsersFromCsvSucceeded(res.data));
  } catch (e) {
    yield put(UsersActions.addUsersFromCsvFailed(e.response.data));
    displayNotification('error', 'Error', 'Error while uploading users');
  }
}

export function* exportActivationIds(action) {
  try {
    const filename = 'activation-links';
    const data = yield call(ApiService.exportActivationIds, action.payload);
    const today = moment.utc(moment.utc(), 'YYYY-MM-DD').format('YYYY-MM-DD');
    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });

    FileSaver.saveAs(blob, `${filename}_${today}.csv`);

    yield put(UsersActions.exportActivationIdsSucceeded(action.payload));
    displayNotification('success', 'Success', 'File is being downloaded');
  } catch (e) {
    yield put(UsersActions.exportActivationIdsFailed(e.response.data));
    displayNotification('error', 'Error', 'Error exporting links');
  }
}

export function* resetPassword(action) {
  try {
    const user = action.payload;
    const res = yield call(ApiService.resetPassword, user);

    yield put(UsersActions.resetPasswordSucceeded(res));
    displayNotification('success', 'Success', 'Password Reset Email was sent successfully');
  } catch (e) {
    yield put(UsersActions.resetPasswordFailed(e.response.data));
    displayNotification('error', 'Error', "Error updating user's Password");
  }
}

export function* removeUser(action) {
  try {
    const user = action.payload;
    const res = yield call(ApiService.removeUser, user);
    yield put(UsersActions.removeUserSucceeded(res));
    displayNotification('success', 'Success', 'User has been removed successfully');
  } catch (e) {
    yield put(UsersActions.removeUserFailed(e.response.data));
    displayNotification('error', 'Error', 'Error removing user');
  }
}

export function* removeUsers(action) {
  try {
    const user = action.payload;
    const res = yield call(ApiService.removeUsers, user);
    yield put(UsersActions.removeUsersSucceeded(res));
    displayNotification(
      'success',
      'Success',
      action.payload.length === 1 ? 'User has been removed successfully' : 'Users have been removed successfully',
    );
  } catch (e) {
    yield put(UsersActions.removeUsersFailed(e.response.data));
    displayNotification('error', 'Error', 'Error removing users');
  }
}

export function* createUser(action) {
  try {
    const user = action.payload;
    const res = yield call(ApiService.addUser, user);
    yield put(UsersActions.createUserSucceeded(res));
    displayNotification('success', 'Success', 'User has been added successfully');
  } catch (e) {
    yield put(UsersActions.createUserFailed(extractError(e.response.data)));
    displayNotification('error', 'Error', 'Error adding new user');
  }
}

export function* exportUserList() {
  const filename = 'users';

  try {
    const { data } = yield call(ApiService.exportUserList);
    const today = moment.utc(moment.utc(), 'YYYY-MM-DD').format('YYYY-MM-DD');
    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });

    FileSaver.saveAs(blob, `${filename}_${today}.csv`);
    yield put(UsersActions.exportUserListSucceeded(data));
  } catch (e) {
    yield put(UsersActions.exportUserListFailed(e.response.data));
    displayNotification('error', 'Error', 'Error exporting users list');
  }
}

export function* inviteUser(action) {
  try {
    const useToInvite = action.payload;
    const res = yield call(ApiService.inviteUser, useToInvite);

    yield put(UsersActions.inviteUserSucceeded(res));
    displayNotification('success', 'Success', 'Invitation was sent successfully');
  } catch (e) {
    yield put(UsersActions.inviteUserFailed());
    displayNotification('error', 'Error', 'There was an error sending invitation');
  }
}

export function* resendInvite(action) {
  try {
    const userId = action.payload;
    const res = yield call(ApiService.sendReminders, userId);

    yield put(UsersActions.resendInviteSucceeded(res));
    displayNotification('success', 'Success', 'Invitation was sent successfully');
  } catch (e) {
    yield put(UsersActions.resendInviteFailed());
    displayNotification('error', 'Error', 'There was an error sending invitation');
  }
}

export function* removeInvite(action) {
  try {
    const inviteId = action.payload;
    const res = yield call(ApiService.removeInvite, { inviteId });
    yield put(UsersActions.removeInviteSucceeded(res));
    displayNotification('success', 'Success', 'Invitation was removed successfully');
  } catch (e) {
    yield put(UsersActions.removeInviteFailed());
    displayNotification('error', 'Error', 'There was an error removing invitation');
  }
}

export function* sendReminders(action) {
  try {
    const users = action.payload;
    const userIds = users.map((user) => user.id);
    const res = yield call(ApiService.sendReminders, userIds);
    yield put(UsersActions.sendRemindersSucceeded(res));
    displayNotification('success', 'Success', 'Reminders were sent successfully');
  } catch (e) {
    yield put(UsersActions.sendRemindersFailed());
    displayNotification('error', 'Error', 'There was an error sending reminders');
  }
}

export function* deleteTeam(action) {
  try {
    const teamId = action.payload;
    const res = yield call(ApiService.deleteTeam, teamId);
    yield put(UsersActions.deleteTeamSucceeded(res));
    displayNotification('success', 'Success', 'Team was deleted successfully');
  } catch (e) {
    yield put(UsersActions.deleteTeamFailed());
    displayNotification('error', 'Error', 'There was an error deleting team');
  }
}

export function* renameTeam(action) {
  try {
    const data = action.payload;
    const res = yield call(ApiService.renameTeam, data);
    yield put(UsersActions.renameTeamSucceeded(res));
    displayNotification('success', 'Success', 'Team was renamed successfully');
  } catch (e) {
    yield put(UsersActions.renameTeamFailed());
    if (e.response.data) {
      displayNotification('error', 'Error', e.response.data[0]);
    } else {
      displayNotification('error', 'Error', 'There was an error renaming team');
    }
  }
}

export function* createTeam(action) {
  try {
    const data = action.payload;
    const res = yield call(ApiService.createTeam, data);
    yield put(UsersActions.createTeamSucceeded(res));
    displayNotification('success', 'Success', 'Team was created successfully');
  } catch (e) {
    yield put(UsersActions.createTeamFailed());
    if (e.response.data.non_field_errors) {
      displayNotification('error', 'Error', e.response.data.non_field_errors[0]);
    } else {
      displayNotification('error', 'Error', 'There was an error creating team');
    }
  }
}
export function* copyActivationLink(action) {
  try {
    const data = action.payload;
    if (data) {
      const res = yield call(ApiService.getActivationLink, data);
      navigator.clipboard.writeText(res);
      yield put(UsersActions.copyActivationLinkSucceeded(res));
      displayNotification('success', 'Success', 'Activation link was copied to the clipboard');
    } else {
      throw new Error('No user id provided');
    }
  } catch (e) {
    yield put(UsersActions.copyActivationLinkFailed());
    displayNotification('error', 'Error', 'There was an error copying link');
  }
}

export function* exportLastThreeMonthAudit() {
  const filename = 'audit_logs';
  try {
    const endDate = new Date();
    const startDate = new Date();

    startDate.setMonth(endDate.getMonth() - 3);
    startDate.setDate(1);
    startDate.setHours(0);
    startDate.setMinutes(0);
    startDate.setSeconds(0);

    const startTimestamp = startDate.valueOf();
    const endTimestamp = endDate.valueOf();
    const timeRange = { startTimestamp, endTimestamp };
    const { data } = yield call(ApiService.exportAuditLogs, timeRange);

    // Month starts from 0
    const startMonth = startDate.getMonth() + 1;
    const startYear = startDate.getFullYear();
    const endMonth = endDate.getMonth() + 1;
    const endYear = endDate.getFullYear();

    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });

    FileSaver.saveAs(blob, `${filename}_${startYear}-${startMonth}_${endYear}-${endMonth}.csv`);
    yield put(UsersActions.exportAuditSucceeded());
  } catch (e) {
    yield put(UsersActions.exportAuditFailed());
    displayNotification('error', 'Error', 'Failed to download audit log');
  }
}

export function* validateCsvFile(action) {
  try {
    const csv = action.payload;
    const res = yield call(ApiService.validateCsv, csv);
    yield put(UsersActions.validateCsvFileSucceeded(res.data));
  } catch (e) {
    yield put(UsersActions.validateCsvFileFailed());
    displayNotification('error', 'Error', 'Failed to validate file');
  }
}

function* onLoad() {
  yield put(UsersActions.fetchUserSummaryRequest());
  yield put(UsersActions.usersFetch());
}

export function* watchUsers() {
  yield SagaUtils.allIfOrgAdmin([
    fork(onLoad),
    takeEvery(
      [
        UsersActions.usersFetch,
        UsersActions.createUserSucceeded,
        UsersActions.removeUserSucceeded,
        UsersActions.removeUsersSucceeded,
        UsersActions.assignToSearchSucceeded,
        UsersActions.unassignToSearchSucceeded,
        UsersActions.adminHandleSucceeded,
        UsersActions.removeInviteSucceeded,
        UsersActions.inviteUserSucceeded,
        UsersActions.resendInviteSucceeded,
        UsersActions.assignToTeamsSucceeded,
        UsersActions.bulkAssignToTeamsSucceeded,
        UsersActions.deleteTeamSucceeded,
        UsersActions.renameTeamSucceeded,
        UsersActions.createTeamSucceeded,
        UsersActions.switchStageToSuccess,
      ],
      fetchUsers,
    ),
    takeLatest([UsersActions.fetchUserSummary, UsersActions.adminHandleSucceeded], fetchUsersSummary),
    takeEvery(UsersActions.adminHandle, adminHandle),
    takeEvery(UsersActions.assignToSearch, searchAssignHandle),
    takeEvery(UsersActions.unassignToSearch, searchUnassignHandle),
    takeEvery(UsersActions.changeUserStatus, userStatusHandle),
    takeEvery(UsersActions.editProfile, editProfile),
    takeEvery(UsersActions.assignToTeams, assignToTeams),
    takeEvery(UsersActions.bulkAssignToTeams, bulkAssignToTeams),
    takeEvery(UsersActions.resetPassword, resetPassword),
    takeEvery(UsersActions.removeUser, removeUser),
    takeEvery(UsersActions.removeUsers, removeUsers),
    takeEvery(UsersActions.createUser, createUser),
    takeEvery(UsersActions.exportUserList, exportUserList),
    takeEvery(UsersActions.inviteUser, inviteUser),
    takeEvery(UsersActions.resendInvite, resendInvite),
    takeEvery(UsersActions.removeInvite, removeInvite),
    takeEvery(UsersActions.sendReminders, sendReminders),
    takeEvery(UsersActions.deleteTeam, deleteTeam),
    takeEvery(UsersActions.renameTeam, renameTeam),
    takeEvery(UsersActions.createTeam, createTeam),
    takeEvery(UsersActions.exportAudit, exportLastThreeMonthAudit),
    takeEvery(UsersActions.exportActivationIds, exportActivationIds),
    takeEvery(UsersActions.copyActivationLink, copyActivationLink),
    takeEvery(UsersActions.validateCsvFile, validateCsvFile),
    takeEvery(UsersActions.addUsersFromCsv, asyncBulkAddUsers),
  ]);
}
